import { nanoid } from 'nanoid'
import * as THREE from 'three'
import type Resources from '~/service/experience/utils/Resources'
import { Nido } from '~/service/experience/world/nido/Nido'
import { NidoCorner } from '~/service/experience/world/nido/Nido'
import { NidoDouble } from '~/service/experience/world/nido/Nido'
import { NidoSingle } from '~/service/experience/world/nido/Nido'
import { NidoStraight } from '~/service/experience/world/nido/Nido'
import { NidoTriple } from '~/service/experience/world/nido/Nido'
import type { Point } from '~/service/experience/world/nido/Point'
import {
  Axis,
  NidoType,
  NidoPlacementSize,
  NidoSeatsCount,
  NidoSocketType,
  NidoGliderType,
  NidoSize,
  NidoSingleSize,
  NidoCageType,
} from '~/service/experience/world/nido/enum'
import {
  isNidoCage,
  isNidoSeat,
  isNidoPillow,
  isNidoLowBackrest,
  isNidoHighBackrest,
} from '~/service/experience/world/nido/helpers'
import type NidoPart from '~/service/experience/world/nido/parts/NidoPart'
import type { NidoSavedParts } from '~/service/experience/world/nido/parts/NidoPart'

let scene: THREE.Scene
let environment: ReturnType<typeof useEnvironment>
let resources: Resources
let room: ReturnType<typeof useRoom>
let nidoObjects: Nido[] = []
let scalesVisible: boolean = false
let circleNidoConfigured: boolean
let socketType: NidoSocketType
let gliderType: NidoGliderType
let arrowHelperLeftX: THREE.ArrowHelper
let arrowHelperRightX: THREE.ArrowHelper
let arrowHelperLeftY: THREE.ArrowHelper
let arrowHelperRightY: THREE.ArrowHelper
let arrowHelperLeftZ: THREE.ArrowHelper
let arrowHelperRightZ: THREE.ArrowHelper
let textScales: Ref<{ position: THREE.Vector3; value: number }[]>
let history: string[]
let historyIndex = -1
let cageType: NidoCageType = NidoCageType.METAL

export default function useWorld(_scene?: THREE.Scene, _resources?: Resources) {
  if (_scene && _resources) {
    environment = useEnvironment(_scene)
    room = useRoom(_scene)

    scene = _scene
    resources = _resources

    resources.on('ready', () => {
      addBranding()
    })

    history = []
    textScales = ref([])

    scalesVisible = false
    circleNidoConfigured = false

    socketType = NidoSocketType.SOCKET_CZ
    gliderType = NidoGliderType.NONE
  }

  function getParts() {
    return nidoObjects
      .map((nido) => nido.getParts())
      .flat(Infinity) as NidoPart[]
  }

  function getNidoCages() {
    return getParts().filter(isNidoCage)
  }

  function getNidoSeats() {
    return getParts().filter(isNidoSeat)
  }

  function getNidoPillows() {
    return getParts().filter(isNidoPillow)
  }

  function getNidoLowBackrests() {
    return getParts().filter(isNidoLowBackrest)
  }

  function getNidoHighBackrests() {
    return getParts().filter(isNidoHighBackrest)
  }

  function setNidoSeatsColor(texture: string) {
    getNidoSeats().forEach((seat) => seat.setColor(texture))
  }

  function setNidoLowBackrestsColor(texture: string) {
    getNidoLowBackrests().forEach((seat) => seat.setColor(texture))
  }

  function setNidoHighBackrestsColor(texture: string) {
    getNidoHighBackrests().forEach((seat) => seat.setColor(texture))
  }

  function setNidoPillowsColor(texture: string) {
    getNidoPillows().forEach((seat) => seat.setColor(texture))
  }

  function setCageType(type: NidoCageType) {
    cageType = type
    nidoObjects.forEach((nido) => {
      nido.setCage()
    })
  }

  function getId(id?: string) {
    return id || nanoid()
  }

  function getBorderCoordinates(
    x: number,
    z: number,
    axis: Axis,
    nidoType: NidoType
  ) {
    let minX: number, maxX: number, minZ: number, maxZ: number
    if (axis === Axis.X) {
      minZ = z - NidoPlacementSize.HALF
      maxZ = z + NidoPlacementSize.HALF
      minX = x - NidoPlacementSize.HALF
      maxX = x + NidoPlacementSize.HALF
      if (nidoType === NidoType.DOUBLE) {
        minX = x - NidoPlacementSize.HALF * NidoSeatsCount.DOUBLE
        maxX = x + NidoPlacementSize.HALF * NidoSeatsCount.DOUBLE
      }
      if (nidoType === NidoType.TRIPLE) {
        minX = x - NidoPlacementSize.HALF * NidoSeatsCount.TRIPLE
        maxX = x + NidoPlacementSize.HALF * NidoSeatsCount.TRIPLE
      }
    } else {
      minZ = z - NidoPlacementSize.HALF
      maxZ = z + NidoPlacementSize.HALF
      minX = x - NidoPlacementSize.HALF
      maxX = x + NidoPlacementSize.HALF
      if (nidoType === NidoType.DOUBLE) {
        minZ = z - NidoPlacementSize.HALF * NidoSeatsCount.DOUBLE
        maxZ = z + NidoPlacementSize.HALF * NidoSeatsCount.DOUBLE
      }
      if (nidoType === NidoType.TRIPLE) {
        minZ = z - NidoPlacementSize.HALF * NidoSeatsCount.TRIPLE
        maxZ = z + NidoPlacementSize.HALF * NidoSeatsCount.TRIPLE
      }
    }

    minX = Math.round(minX * 100) / 100
    maxX = Math.round(maxX * 100) / 100
    minZ = Math.round(minZ * 100) / 100
    maxZ = Math.round(maxZ * 100) / 100

    return { minX, maxX, minZ, maxZ }
  }

  function getNidoBorderCoordinates(nido: Nido) {
    let {
      lowX: minX,
      highX: maxX,
      lowZ: minZ,
      highZ: maxZ,
    } = nido.getPlacementScales()

    minX = Math.round((nido.instance.position.x + minX) * 100) / 100
    maxX = Math.round((nido.instance.position.x + maxX) * 100) / 100
    minZ = Math.round((nido.instance.position.z + minZ) * 100) / 100
    maxZ = Math.round((nido.instance.position.z + maxZ) * 100) / 100

    return { minX, maxX, minZ, maxZ }
  }

  function canAddNido(neighbor: Nido, nidoType: NidoType, point: Point) {
    const offset = getOffset(neighbor, nidoType, point)
    const { x, z, axis } = getPosition(offset, neighbor, point)
    const {
      minX: minAx,
      maxX: maxAx,
      minZ: minAz,
      maxZ: maxAz,
    } = getBorderCoordinates(x, z, axis, nidoType)

    const {
      minX: minNx,
      maxX: maxNx,
      minZ: minNz,
      maxZ: maxNz,
    } = getNidoBorderCoordinates(neighbor)

    let overlap = false

    nidoObjects.forEach((nido) => {
      if (
        nido.id === neighbor.id ||
        (neighbor instanceof NidoCorner &&
          (nido.id === neighbor.neighborX?.id ||
            nido.id === neighbor.neighborZ?.id))
      ) {
        return
      }

      const {
        minX: minBx,
        maxX: maxBx,
        minZ: minBz,
        maxZ: maxBz,
      } = getNidoBorderCoordinates(nido)

      // B is on the right of A
      // OR B is on the left of A
      // OR B is below A
      // OR B is on top of A
      // AND they are not touching (there needs to be space of at least one single Nido between not connected Nidos)
      if (maxAx < minBx || minAx > maxBx || maxAz < minBz || minAz > maxBz) {
        // no collision, continue
        return
      }

      // check possible connection with corner
      if (nido instanceof NidoCorner) {
        // cannot have corner touching another corner in any way
        if (nidoType === NidoType.CORNER) {
          overlap = true
          return
        }

        // minAx === minBx && maxAx === maxBx means Nidos are in one line in Z axis
        if (minAx === minBx && maxAx === maxBx) {
          if (nido.instance.position.z < z) {
            // current nido is in axis before
            if (nido.invertedZ) {
              // target nido is facing bad direction, cannot connect
              overlap = true
            }
          } else if (!nido.invertedZ) {
            // current nido is in axis after and is facing bad direction, cannot connect
            overlap = true
          }
        } else if (minAz === minBz && maxAz === maxBz) {
          // minAz === minBz && maxAz === maxBz means Nidos are in one line in X axis
          if (nido.instance.position.x < x) {
            // current nido is in axis before
            if (nido.invertedX) {
              // target nido is facing bad direction, cannot connect
              overlap = true
            }
          } else if (!nido.invertedX) {
            // current nido is in axis after and is facing bad direction, cannot connect
            overlap = true
          }
        } else {
          // Nidos are not in one line, cannot connect
          // but they must be touching somehow, that means there is some overlap
          overlap = true
        }
      } else {
        // else current nido is not corner and Nidos are touching with at least one point or overlapping entirely
        // Nidos cannot be touching with two points, that would mean they are already connected

        // minAx === maxBx && minAz === maxBz means Nidos are touching with just one point, B is to the top left of A
        // maxAx === minBx && minAz === maxBz means Nidos are touching with just one point, B is to the top right of A
        // minAx === maxBx && maxAz === minBz means Nidos are touching with just one point, B is to the bottom left of A
        // maxAx === minBx && maxAz === minBz means Nidos are touching with just one point, B is to the bottom right of A
        if (
          (minAx === maxBx && minAz === maxBz) ||
          (maxAx === minBx && minAz === maxBz) ||
          (minAx === maxBx && maxAz === minBz) ||
          (maxAx === minBx && maxAz === minBz)
        ) {
          // no collision, continue
          return
        }

        // check whether we can add corner, we will rotate it the right way later (if needed)
        if (nidoType === NidoType.CORNER) {
          // corner can be added when it is touching with Nido on the left or right (there cannot be a Nido straight in front of it)
          // have to compare it to neighbour of Nido we are adding corner to because corner is always in line with its neighbour

          // B is on top of N (touching)
          // OR B is below N (touching)
          if (minNz >= maxBz || maxNz <= minBz) {
            if (maxBx <= minNx || maxNx <= minBx) {
              // B is to the left or to the right of N not directly in front of below
              // that means corner can be added
              return
            }
            overlap = true
          }

          // OR B is on the left of N (touching)
          // OR B is on the right of N (touching)
          if (minNx <= maxBx || maxNx <= minBx) {
            if (maxBz <= minNz || maxNz <= minBz) {
              // B is on top or below N not directly to the left or right
              // that means corner can be added
              return
            }
            overlap = true
          }
          overlap = true
        } else {
          // we are adding straight nido, can add it if it is in line and would just touch another nido

          // Nidos are in one line in Z axis and they touch (they touch with 2 points) and nido has right rotation (axis)
          // minAx === minBx && maxAx === maxBx means Nidos are in one line in Z axis
          // maxAz === minBz means A is on top of B
          // minAz === maxBz means B is on top of A
          if (
            minAx === minBx &&
            maxAx === maxBx &&
            (maxAz === minBz || minAz === maxBz) &&
            nido.axis === Axis.Z
          ) {
            if (neighbor instanceof NidoCorner && neighbor.axis === Axis.X) {
              // corner will rotate axis to Z
              return
            }
            if (neighbor.axis === Axis.Z) {
              return
            }
          }

          // Nidos are in one line in X axis and they touch and nido has right rotation (axis)
          // minAz === minBz && maxAz === maxBz means Nidos are in one line in X axis
          // maxAx === minBx means B is second (A, B)
          // minAx === maxBc means B is first (B, A)
          if (
            minAz === minBz &&
            maxAz === maxBz &&
            (maxAx === minBx || minAx === maxBx) &&
            nido.axis === Axis.X
          ) {
            if (neighbor instanceof NidoCorner && neighbor.axis === Axis.Z) {
              // corner will rotate axis to X
              return
            }

            if (neighbor.axis === Axis.X) {
              return
            }
          }

          overlap = true
        }
      }
    })

    return !overlap
  }

  function getPossibleNeighbour(nido: Nido, nidoType: NidoType, point: Point) {
    const { x, z, axis } = getPosition(0, nido, point)
    const {
      minX: minAx,
      maxX: maxAx,
      minZ: minAz,
      maxZ: maxAz,
    } = getBorderCoordinates(x, z, axis, nidoType)

    for (const curNido of nidoObjects) {
      if (
        curNido.id === nido.id ||
        (nido instanceof NidoCorner &&
          (curNido.id === nido.neighborX?.id ||
            curNido.id === nido.neighborZ?.id)) ||
        (curNido instanceof NidoCorner &&
          (nido.id === curNido.neighborX?.id ||
            nido.id === curNido.neighborZ?.id)) ||
        (curNido instanceof NidoStraight &&
          (nido.id === curNido.neighborLeft?.id ||
            nido.id === curNido.neighborRight?.id)) ||
        (nido instanceof NidoStraight &&
          (curNido.id === nido.neighborLeft?.id ||
            curNido.id === nido.neighborRight?.id))
      ) {
        // filter out existing neighbours
        continue
      }

      const {
        minX: minBx,
        maxX: maxBx,
        minZ: minBz,
        maxZ: maxBz,
      } = getNidoBorderCoordinates(curNido)

      // Nidos are in one line in Z axis and they touch (they touch with 2 points)
      // minAx === minBx && maxAx === maxBx means Nidos are in one line in Z axis
      // maxAz === minBz means A is on top of B
      // minAz === maxBz means B is on top of A
      // OR Nidos are in one line in X axis and they touch
      // minAz === minBz && maxAz === maxBz means Nidos are in one line in X axis
      // maxAx === minBx means B is second (A, B)
      // minAx === maxBc means B is first (B, A)
      if (
        (minAx === minBx &&
          maxAx === maxBx &&
          (maxAz === minBz || minAz === maxBz)) ||
        (minAz === minBz &&
          maxAz === maxBz &&
          (maxAx === minBx || minAx === maxBx))
      ) {
        if (curNido instanceof NidoCorner) {
          if (axis === Axis.Z) {
            if (curNido.instance.position.z < z) {
              // current nido is in axis before
              if (!curNido.invertedZ) {
                // target nido is facing right direction
                return curNido
              }
            } else if (curNido.invertedZ) {
              // target nido is facing right direction
              return curNido
            }
          } else if (curNido.instance.position.x < x) {
            // current nido is in axis before
            if (!curNido.invertedX) {
              // target nido is facing right direction
              return curNido
            }
          } else if (curNido.invertedX) {
            // target nido is facing right direction
            return curNido
          }
        } else {
          // we are either adding straight Nido or Corner (does not matter)
          // we know that curNido is in line with new corner Nido, it is touching it and it is not its neighbour yet
          // when we are adding corner we might have to rotate it, however that is handled elsewhere
          return curNido
        }
      }
    }
    return null
  }

  function loadNidoSingle(
    x: number,
    y: number,
    z: number,
    axis: Axis,
    parts: NidoSavedParts,
    id: string,
    seatCategory: number
  ) {
    const nido = new NidoSingle(id, { x, y, z }, axis, parts, seatCategory)
    nidoObjects.push(nido)
  }

  function createNidoSingle(x: number, y: number, z: number, axis: Axis) {
    const nido = new NidoSingle(getId(), { x, y, z }, axis)
    nidoObjects.push(nido)
    return nido
  }

  function addNidoSingle(neighbor: Nido, point: Point) {
    const offset = getOffset(neighbor, NidoType.SINGLE, point)
    const { x, y, z, axis } = getPosition(offset, neighbor, point)

    const nido = createNidoSingle(x, y, z, axis)

    if (neighbor) {
      neighbor.addNeighbor(nido)
      nido.addNeighbor(neighbor)
    }

    const secondNeighbour = getPossibleNeighbour(nido, NidoType.SINGLE, point)
    if (secondNeighbour !== null) {
      secondNeighbour.addNeighbor(nido)
      nido.addNeighbor(secondNeighbour)
      circleNidoConfigured = true
    }

    const configurator = useConfigurator()
    configurator.addedChair()
  }

  function loadNidoDouble(
    x: number,
    y: number,
    z: number,
    axis: Axis,
    parts: NidoSavedParts,
    id: string,
    seatCategory: number
  ) {
    const nido = new NidoDouble(id, { x, y, z }, axis, parts, seatCategory)
    nidoObjects.push(nido)
  }

  function createNidoDouble(x: number, y: number, z: number, axis: Axis) {
    const nido = new NidoDouble(getId(), { x, y, z }, axis)
    nidoObjects.push(nido)
    return nido
  }

  function addNidoDouble(neighbor: Nido, point: Point) {
    const offset = getOffset(neighbor, NidoType.DOUBLE, point)
    const { x, y, z, axis } = getPosition(offset, neighbor, point)

    const nido = createNidoDouble(x, y, z, axis)

    if (neighbor) {
      neighbor.addNeighbor(nido)
      nido.addNeighbor(neighbor)
    }

    const secondNeighbour = getPossibleNeighbour(nido, NidoType.DOUBLE, point)
    if (secondNeighbour !== null) {
      secondNeighbour.addNeighbor(nido)
      nido.addNeighbor(secondNeighbour)
      circleNidoConfigured = true
    }

    const configurator = useConfigurator()
    configurator.addedChair()
  }

  function loadNidoTriple(
    x: number,
    y: number,
    z: number,
    axis: Axis,
    parts: NidoSavedParts,
    id: string,
    seatCategory: number
  ) {
    const nido = new NidoTriple(id, { x, y, z }, axis, parts, seatCategory)
    nidoObjects.push(nido)
  }

  function createNidoTriple(x: number, y: number, z: number, axis: Axis) {
    const nido = new NidoTriple(getId(), { x, y, z }, axis)
    nidoObjects.push(nido)
    return nido
  }

  function addNidoTriple(neighbor: Nido, point: Point) {
    const offset = getOffset(neighbor, NidoType.TRIPLE, point)
    const { x, y, z, axis } = getPosition(offset, neighbor, point)

    const nido = createNidoTriple(x, y, z, axis)

    if (neighbor) {
      neighbor.addNeighbor(nido)
      nido.addNeighbor(neighbor)
    }

    const secondNeighbour = getPossibleNeighbour(nido, NidoType.TRIPLE, point)
    if (secondNeighbour !== null) {
      secondNeighbour.addNeighbor(nido)
      nido.addNeighbor(secondNeighbour)
      circleNidoConfigured = true
    }

    const configurator = useConfigurator()
    configurator.addedChair()
  }

  function loadNidoCorner(
    x: number,
    y: number,
    z: number,
    axis: Axis,
    invertedX: boolean,
    invertedZ: boolean,
    parts: NidoSavedParts,
    id: string,
    seatCategory: number
  ) {
    const nido = new NidoCorner(
      id,
      { x, y, z },
      axis,
      invertedX,
      invertedZ,
      parts,
      seatCategory
    )
    nidoObjects.push(nido)
  }

  function createNidoCorner(
    x: number,
    y: number,
    z: number,
    axis: Axis,
    invertedX: boolean,
    invertedZ: boolean
  ) {
    const nido = new NidoCorner(
      getId(),
      { x, y, z },
      axis,
      invertedX,
      invertedZ
    )
    nidoObjects.push(nido)
    return nido
  }

  function addNidoCorner(neighbor: Nido, point: Point) {
    const offset = getOffset(neighbor, NidoType.CORNER, point)
    const { x, y, z, axis } = getPosition(offset, neighbor, point)

    const nido = createNidoCorner(
      x,
      y,
      z,
      axis,
      axis === Axis.X ? offset > 0 : false,
      axis === Axis.Z ? offset > 0 : false
    )

    if (neighbor) {
      neighbor.addNeighbor(nido)
      nido.addNeighbor(neighbor)
    }

    const secondNeighbour = getPossibleNeighbour(nido, NidoType.CORNER, point)
    if (secondNeighbour !== null) {
      // need to invert opposite axis of corner axis
      const negatedAxis = axis === Axis.X ? Axis.Z : Axis.X
      if (axis === Axis.Z) {
        if (secondNeighbour.instance.position.x < x) {
          // second neighbour is to the left of corner, corner has to be inverted in X
          if (!nido.invertedX) {
            nido.invert(negatedAxis)
          }
        } else if (nido.invertedX) {
          // second neighbour is to the right of corner, corner should not be inverted
          nido.invert(negatedAxis)
        }
      } else if (secondNeighbour.instance.position.z < z) {
        // second neighbour is on top of corner, corner has to be inverted in Z
        if (!nido.invertedZ) {
          nido.invert(negatedAxis)
        }
      } else if (nido.invertedZ) {
        // second neighbour is below corner, corner should not be inverted
        nido.invert(negatedAxis)
      }
      secondNeighbour.addNeighbor(nido)
      nido.addNeighbor(secondNeighbour)
      circleNidoConfigured = true
    }

    const configurator = useConfigurator()
    configurator.addedChair()
  }

  function changeSocketType(newSocketType: NidoSocketType) {
    socketType = newSocketType
    nidoObjects.forEach((nido) => {
      nido.changeSocketType(newSocketType)
    })
  }

  function changeGliderType(newGliderType: NidoGliderType) {
    gliderType = newGliderType
  }

  function getOffset(curNido: Nido, newNidoType: NidoType, point: Point) {
    let offset = 1

    switch (true) {
      case curNido instanceof NidoDouble:
        offset += 0.5
        break
      case curNido instanceof NidoTriple:
        offset += 1
    }

    switch (newNidoType) {
      case NidoType.DOUBLE:
        offset += 0.5
        break
      case NidoType.TRIPLE:
        offset += 1
    }

    if (
      point.position.x < curNido.instance.position.x ||
      point.position.z < curNido.instance.position.z
    ) {
      offset *= -1
    }

    return offset
  }

  function getPosition(offset: number, nido: Nido, point: Point) {
    // Get coordinates and axis
    let x = nido.instance.position.x
    const y = nido.instance.position.y
    let z = nido.instance.position.z
    let axis = nido.axis

    // Add offset
    if (nido instanceof NidoCorner) {
      if (point.position.x !== nido.instance.position.x) {
        x += NidoPlacementSize.UNIT * offset
        axis = Axis.X
      } else {
        z += NidoPlacementSize.UNIT * offset
        axis = Axis.Z
      }
    } else if (nido.axis === Axis.X) {
      x += NidoPlacementSize.UNIT * offset
    } else {
      z += NidoPlacementSize.UNIT * offset
    }

    return { x, y, z, axis }
  }

  function addBranding() {
    const logoPosition = 10.05

    addLogo(0, 1.5, -logoPosition, Math.PI / 2)
    addLogo(0, 1.5, logoPosition, -Math.PI / 2)
    addLogo(-logoPosition, 1.5, 0, Math.PI)
    addLogo(logoPosition, 1.5, 0, 0)
  }

  function addLogo(x: number, y: number, z: number, rotationY: number) {
    const logo = resources.models.ldsLogo.clone(true)

    logo.position.x = x
    logo.position.y = y
    logo.position.z = z
    logo.rotation.y = rotationY
    // logo.traverse((object) => {
    //   object.castShadow = true
    // })
    scene.add(logo)
  }

  function getNido(id: string) {
    // TODO: Maybe return NULL OBJECT, so we don't have to handle undefined (call method without any effect on it)
    return nidoObjects.find((nido) => nido.id === id) as Nido
  }

  function removeNido(nido: Nido) {
    nido.remove()

    // Remove model from THREE scene
    scene.remove(nido.instance)
    scene.remove(nido.collider)

    // Remove instance from Nido objects
    const index = nidoObjects.indexOf(nido)
    if (index !== -1) {
      nidoObjects.splice(index, 1)
    }
    // if there was circle of Nidos it is no more
    circleNidoConfigured = false

    const configurator = useConfigurator()
    configurator.addedChair()
  }

  function save() {
    if (history.length > 20) {
      history.shift()
    }

    if (historyIndex !== history.length - 2) {
      history.splice(historyIndex + 2, history.length - historyIndex + 2)
    }
    history.push(toJSON())

    historyIndex = history.length - 2
  }

  function undo() {
    const state = history[historyIndex]

    if (!state) return

    if (historyIndex !== -1) {
      historyIndex--
    }

    clear()
    load(state)
  }

  function redo() {
    const state = history[historyIndex + 2]

    if (!state) return

    if (historyIndex !== history.length - 2) {
      historyIndex++
    }

    clear()
    load(state)
  }

  function clear() {
    for (let i = nidoObjects.length - 1; i >= 0; i--) {
      removeNido(nidoObjects[i])
    }
  }

  function clearHistory() {
    history = []
  }

  function toJSON() {
    const nidos: string[] = []
    nidoObjects.forEach((nido) => {
      nidos.push(nido.toJSON())
    })

    const obj = {
      circleNidoConfigured,
      gliderType,
      nidos,
      cageType,
    }

    return JSON.stringify(obj)
  }

  function load(json: string) {
    const parsed = JSON.parse(json)

    circleNidoConfigured = parsed.circleNidoConfigured
    gliderType = parsed.gliderType ?? NidoGliderType.NONE
    cageType = parsed.cageType ?? NidoCageType.METAL

    const nidoIds: any[] = []

    parsed.nidos.forEach((nidoStr: string) => {
      const nido = JSON.parse(nidoStr)

      nidoIds.push({ id: nido.id, neighborIds: nido.neighborIds })

      switch (nido.type) {
        case NidoType.SINGLE:
          loadNidoSingle(
            nido.position.x,
            nido.position.y,
            nido.position.z,
            nido.axis,
            nido.parts,
            nido.id,
            nido.seatCategory
          )
          break
        case NidoType.DOUBLE:
          loadNidoDouble(
            nido.position.x,
            nido.position.y,
            nido.position.z,
            nido.axis,
            nido.parts,
            nido.id,
            nido.seatCategory
          )
          break
        case NidoType.TRIPLE:
          loadNidoTriple(
            nido.position.x,
            nido.position.y,
            nido.position.z,
            nido.axis,
            nido.parts,
            nido.id,
            nido.seatCategory
          )
          break
        case NidoType.CORNER:
          loadNidoCorner(
            nido.position.x,
            nido.position.y,
            nido.position.z,
            nido.axis,
            nido.invertedX,
            nido.invertedZ,
            nido.parts,
            nido.id,
            nido.seatCategory
          )
          break
      }
    })

    nidoIds.forEach((ids) => {
      const nido = getNido(ids.id)

      ids.neighborIds.forEach((id: string) => {
        const neighbor = getNido(id)
        if (neighbor) nido?.addNeighbor(neighbor)
      })
    })

    const configurator = useConfigurator()
    configurator.addedChair()
  }

  function getXScale() {
    let lowestX = Infinity
    let highestX = -Infinity

    nidoObjects.forEach((nidoObject) => {
      const offset = nidoObject.getRealScales()

      const lowestDiff = nidoObject.instance.position.x + offset.lowX
      const highestDiff = nidoObject.instance.position.x + offset.highX
      if (lowestDiff < lowestX) {
        lowestX = lowestDiff
      }
      if (highestDiff > highestX) {
        highestX = highestDiff
      }
    })

    return { lowestX, highestX }
  }

  function getZScale() {
    let lowestZ = Infinity
    let highestZ = -Infinity

    nidoObjects.forEach((nidoObject) => {
      const offset = nidoObject.getRealScales()

      const lowestDiff = nidoObject.instance.position.z + offset.lowZ
      const highestDiff = nidoObject.instance.position.z + offset.highZ
      if (lowestDiff < lowestZ) {
        lowestZ = lowestDiff
      }
      if (highestDiff > highestZ) {
        highestZ = highestDiff
      }
    })

    return { lowestZ, highestZ }
  }

  function getYScale() {
    let highestY = -Infinity

    nidoObjects.forEach((nidoObject) => {
      const offset = nidoObject.getRealScales()

      if (highestY < offset.y) {
        highestY = offset.y
      }
    })

    return highestY
  }

  function addScales() {
    const { lowestX, highestX } = getXScale()
    const { lowestZ, highestZ } = getZScale()
    const highestY = getYScale()

    // TODO: Maybe refactor with one function?

    const toHighestX = new THREE.Vector3(highestX, 0, 0)
    const toLowestX = new THREE.Vector3(lowestX, 0, 0)
    const distanceX = Math.sqrt(Math.pow(highestX - lowestX, 2))
    arrowHelperRightX = new THREE.ArrowHelper(
      new THREE.Vector3(1, 0, 0),
      toLowestX,
      distanceX,
      '#000',
      0.15,
      0.025
    )
    arrowHelperRightX.position.y = 0.05
    arrowHelperRightX.position.z = highestZ + 0.5
    arrowHelperRightX.name = 'Vertical Line Up'
    arrowHelperLeftX = new THREE.ArrowHelper(
      new THREE.Vector3(-1, 0, 0),
      toHighestX,
      distanceX,
      '#000',
      0.15,
      0.025
    )
    arrowHelperLeftX.position.y = 0.05
    arrowHelperLeftX.position.z = highestZ + 0.5
    arrowHelperLeftX.name = 'Vertical Line Down'
    scene.add(arrowHelperRightX, arrowHelperLeftX)
    textScales.value.push({
      position: new THREE.Vector3(
        highestX - distanceX / 2,
        0.1,
        highestZ + NidoSize.DEFAULT_DEPTH
      ),
      value: distanceX,
    })

    const toHighestY = new THREE.Vector3(0, highestY, 0)
    const toLowestY = new THREE.Vector3(0, 0, 0)
    const distanceY = highestY
    const halfDistanceY = distanceY / 2
    arrowHelperRightY = new THREE.ArrowHelper(
      new THREE.Vector3(0, 1, 0),
      toLowestY,
      halfDistanceY,
      '#000',
      0.15,
      0.025
    )
    arrowHelperRightY.position.y = halfDistanceY
    arrowHelperRightY.position.x = highestX + 0.5
    arrowHelperRightY.position.z = highestZ + 0.5
    arrowHelperRightY.name = 'Vertical Line Up'
    arrowHelperLeftY = new THREE.ArrowHelper(
      new THREE.Vector3(0, -1, 0),
      toHighestY,
      halfDistanceY,
      '#000',
      0.15,
      0.025
    )
    arrowHelperLeftY.position.y = halfDistanceY
    arrowHelperLeftY.position.x = highestX + 0.5
    arrowHelperLeftY.position.z = highestZ + 0.5
    arrowHelperLeftY.name = 'Vertical Line Down'
    scene.add(arrowHelperRightY, arrowHelperLeftY)
    textScales.value.push({
      position: new THREE.Vector3(
        highestX + NidoSingleSize.WIDTH,
        halfDistanceY,
        highestZ + NidoSize.DEFAULT_DEPTH
      ),
      value: distanceY,
    })

    const toHighestZ = new THREE.Vector3(0, 0, highestZ)
    const toLowestZ = new THREE.Vector3(0, 0, lowestZ)
    const distanceZ = Math.sqrt(Math.pow(highestZ - lowestZ, 2))
    arrowHelperRightZ = new THREE.ArrowHelper(
      new THREE.Vector3(0, 0, 1),
      toLowestZ,
      distanceZ,
      '#000',
      0.15,
      0.025
    )
    arrowHelperRightZ.position.y = 0.05
    arrowHelperRightZ.position.x = highestX + 0.5
    arrowHelperRightZ.name = 'Vertical Line Up'
    arrowHelperLeftZ = new THREE.ArrowHelper(
      new THREE.Vector3(0, 0, -1),
      toHighestZ,
      distanceZ,
      '#000',
      0.15,
      0.025
    )
    arrowHelperLeftZ.position.y = 0.05
    arrowHelperLeftZ.position.x = highestX + 0.5
    arrowHelperLeftZ.name = 'Vertical Line Down'
    scene.add(arrowHelperRightZ, arrowHelperLeftZ)
    textScales.value.push({
      position: new THREE.Vector3(
        highestX + NidoSingleSize.WIDTH,
        0.1,
        highestZ - distanceZ / 2
      ),
      value: distanceZ,
    })

    scalesVisible = true
  }

  function setTextScales(
    scales: Ref<{ position: THREE.Vector3; value: number }[]>
  ) {
    textScales = scales
  }

  function removeScales() {
    scene.remove(
      arrowHelperLeftX,
      arrowHelperRightX,
      arrowHelperLeftY,
      arrowHelperRightY,
      arrowHelperLeftZ,
      arrowHelperRightZ
    )

    textScales.value = []

    scalesVisible = false
  }

  function toggleScales() {
    if (scalesVisible) {
      removeScales()
    } else {
      addScales()
    }

    const raycaster = useRaycaster()
    raycaster.checkPoints()
  }

  function getScaleVisibility() {
    return scalesVisible
  }

  function getSocketType() {
    return socketType
  }

  function destroy() {
    environment.destroy()
  }

  return {
    cageType,
    destroy,
    setTextScales,
    addNidoSingle,
    addNidoDouble,
    addNidoTriple,
    addNidoCorner,
    loadNidoSingle,
    loadNidoDouble,
    loadNidoTriple,
    loadNidoCorner,
    createNidoSingle,
    createNidoDouble,
    createNidoTriple,
    createNidoCorner,
    getNido,
    removeNido,
    changeSocketType,
    changeGliderType,
    undo,
    redo,
    clear,
    clearHistory,
    toJSON,
    load,
    toggleScales,
    getScaleVisibility,
    addBranding,
    save,
    getSocketType,
    removeScales,
    addScales,
    canAddNido,
    setNidoHighBackrestsColor,
    setNidoLowBackrestsColor,
    setNidoSeatsColor,
    setNidoPillowsColor,
    setCageType,
    scalesVisible,
    nidoObjects,
    textScales,
    circleNidoConfigured,
  }
}
